import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { usePermissionDeniedStore } from '../stores/permissionDenied.store';
import { CreateTagDto, TagDto, UpdateTagDto } from '@prometeus/contracts';
import { Breadcrumb } from '../components/Breadcrumb';

export const Route = createFileRoute('/w/$workspaceId/settings/tags')({
  component: TagsPage,
});

function TagsPage() {
  const { workspaceId } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const { requirePermission } = usePermissionGuard();
  const showPermissionDenied = usePermissionDeniedStore((state) => state.show);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTag, setEditingTag] = useState<TagDto | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const canRead = hasPermission('tags.read');

  useEffect(() => {
    if (!location.pathname.startsWith('/w/')) return;
    if (!canRead) {
      showPermissionDenied('Ver tags');
      navigate({ to: '/w/$workspaceId/clients', params: { workspaceId } });
    }
  }, [location.pathname, canRead, showPermissionDenied, navigate, workspaceId]);

  const { data: tags, isLoading } = useQuery<TagDto[]>({
    queryKey: ['tags', workspaceId],
    queryFn: async () => {
      const response = await api.get('/tags');
      return response.data.data;
    },
    enabled: canRead,
  });

  const { data: workspace } = useQuery({
    queryKey: ['workspaces', workspaceId],
    queryFn: async () => {
      const response = await api.get(`/workspaces/${workspaceId}`);
      return response.data.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (dto: CreateTagDto) => {
      const response = await api.post('/tags', dto);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags', workspaceId] });
      setShowCreateModal(false);
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Erro ao criar tag');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: UpdateTagDto }) => {
      const response = await api.put(`/tags/${id}`, dto);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags', workspaceId] });
      setEditingTag(null);
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Erro ao atualizar tag');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/tags/${id}`);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags', workspaceId] });
      setDeleteConfirmId(null);
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Erro ao deletar tag');
    },
  });

  if (!canRead) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-8 py-6">
        <Breadcrumb
          items={[
            { label: workspace?.name || 'Workspace', to: '/w/$workspaceId/clients', params: { workspaceId } },
            { label: 'Configurações', to: '/w/$workspaceId/settings/members', params: { workspaceId } },
            { label: 'Gestão de Tags' },
          ]}
        />
        
        <div 
          className="bg-white p-6 mt-6"
          style={{
            borderRadius: '15px',
            boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
          }}
        >
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#2D3748',
                  marginBottom: '0.25rem',
                }}
              >
                Gestão de Tags
              </h1>
              <p 
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  color: '#A0AEC0',
                  fontWeight: 400,
                }}
              >
                Gerencie as tags usadas para categorizar processos
              </p>
            </div>
            <button
              onClick={() => {
                if (!requirePermission('tags.create', 'Criar tags')) return;
                setShowCreateModal(true);
              }}
              className="rounded-lg transition-colors hover:opacity-80"
                style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#F9FAFB',
                  boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title="Nova Tag"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 5V19M5 12H19"
                    stroke="#2D3748"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
          </div>

          {isLoading ? (
            <div 
              className="p-8 text-center"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '12px',
                color: '#718096',
              }}
            >
              Carregando tags...
            </div>
          ) : tags && tags.length > 0 ? (
            <div className="space-y-2">
              {tags.map((tag) => (
                <div 
                  key={tag.id} 
                  className="p-4 flex items-center justify-between rounded-lg transition-colors hover:bg-gray-50"
                  style={{
                    backgroundColor: '#F9FAFB',
                    boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-6 h-6 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.color }}
                    />
                    <div className="flex flex-col">
                      <span 
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#2D3748',
                        }}
                      >
                        {tag.name}
                      </span>
                      <span 
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '10px',
                          color: '#718096',
                        }}
                      >
                        {tag.color}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        if (!requirePermission('tags.update', 'Editar tags')) return;
                        setEditingTag(tag);
                      }}
                      className="px-3 py-1.5 rounded transition-colors hover:opacity-80"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        backgroundColor: '#F9FAFB',
                        color: '#2D3748',
                      }}
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => {
                        if (!requirePermission('tags.delete', 'Excluir tags')) return;
                        setDeleteConfirmId(tag.id);
                      }}
                      className="px-3 py-1.5 rounded transition-colors hover:opacity-80"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        backgroundColor: '#F9FAFB',
                        color: '#EF4444',
                      }}
                    >
                      Excluir
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <p 
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '14px',
                  color: '#718096',
                  marginBottom: '0.5rem',
                }}
              >
                Nenhuma tag encontrada
              </p>
              <p 
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  color: '#A0AEC0',
                  marginBottom: '1.5rem',
                }}
              >
                Nenhuma tag foi criada ainda.
              </p>
              <button
                onClick={() => {
                  if (!requirePermission('tags.create', 'Criar tags')) return;
                  setShowCreateModal(true);
                }}
                className="px-4 py-2 rounded-lg transition-colors hover:opacity-80"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  backgroundColor: '#03131B',
                  color: '#FFFFFF',
                }}
              >
                Criar Primeira Tag
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <TagModal
          onClose={() => setShowCreateModal(false)}
          onSave={(dto) => {
            if ('name' in dto && dto.name) {
              if (!requirePermission('tags.create', 'Criar tags')) return;
              createMutation.mutate({ name: dto.name, color: dto.color });
            }
          }}
          isSaving={createMutation.isPending}
        />
      )}

      {/* Edit Modal */}
      {editingTag && (
        <TagModal
          tag={editingTag}
          onClose={() => setEditingTag(null)}
          onSave={(dto) => {
            if (!requirePermission('tags.update', 'Editar tags')) return;
            updateMutation.mutate({ id: editingTag.id, dto });
          }}
          isSaving={updateMutation.isPending}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && createPortal(
        <div 
          className="fixed inset-0 flex items-center justify-center z-[9999]"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={() => setDeleteConfirmId(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-confirm-modal-title"
        >
          <div 
            className="bg-white dark:bg-gray-800 max-w-md w-full mx-4 rounded-[15px] flex flex-col overflow-hidden"
            style={{
              fontFamily: "'Poppins', sans-serif",
              boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header – padrão da plataforma */}
            <div
              className="flex items-center justify-between gap-4 flex-shrink-0 p-5 pb-4 dark:border-gray-700"
              style={{ borderBottom: '1px solid rgba(6, 20, 27, 0.08)' }}
            >
              <h3 
                id="delete-confirm-modal-title"
                className="dark:text-gray-100"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#06141B',
                }}
              >
                Confirmar exclusão
              </h3>
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="w-8 h-8 flex items-center justify-center rounded-[10px] transition-opacity hover:opacity-80"
                style={{ backgroundColor: '#06141B', color: '#F5F6F5' }}
                aria-label="Fechar"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Conteúdo */}
            <div className="p-5 flex-1">
              <p 
                className="dark:text-gray-300"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: 12,
                  fontWeight: 400,
                  color: '#2D3748',
                  lineHeight: 1.5,
                }}
              >
                Tem certeza que deseja excluir esta tag? Esta ação não pode ser desfeita.
              </p>
            </div>

            {/* Footer – botões no padrão da plataforma */}
            <div className="flex gap-3 p-5 pt-4 border-t border-gray-100 dark:border-gray-700 flex-shrink-0 justify-end">
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="px-[14px] py-2 rounded-[8px] transition-colors hover:bg-gray-50 dark:hover:bg-gray-700"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#2D3748',
                  border: '1px solid #E2E8F0',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!requirePermission('tags.delete', 'Excluir tags')) return;
                  deleteMutation.mutate(deleteConfirmId);
                }}
                disabled={deleteMutation.isPending}
                className="px-[14px] py-2 rounded-[8px] transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: 12,
                  fontWeight: 500,
                  backgroundColor: '#DC2626',
                  color: '#FFFFFF',
                  border: 'none',
                  cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
                }}
              >
                {deleteMutation.isPending ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

interface TagModalProps {
  tag?: TagDto;
  onClose: () => void;
  onSave: (dto: CreateTagDto | UpdateTagDto) => void;
  isSaving: boolean;
}

const TAG_COLOR_PALETTE = [
  '#06141B', '#DC2626', '#EA580C', '#CA8A04', '#65A30D', '#059669', '#0D9488', '#0891B2',
  '#2563EB', '#7C3AED', '#DB2777', '#4B5563', '#78716C', '#3B82F6', '#10B981', '#EAB308',
];

function TagModal({ tag, onClose, onSave, isSaving }: TagModalProps) {
  const [name, setName] = useState(tag?.name || '');
  const [color, setColor] = useState(tag?.color || '#3B82F6');
  const [focusedInput, setFocusedInput] = useState<string | null>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);

  const normalizedHex = (value: string) => {
    const v = value.replace(/^#/, '').trim();
    if (/^[0-9A-Fa-f]{6}$/.test(v)) return `#${v}`;
    if (/^[0-9A-Fa-f]{3}$/.test(v)) return `#${v[0]}${v[0]}${v[1]}${v[1]}${v[2]}${v[2]}`;
    return value;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      alert('Nome da tag é obrigatório');
      return;
    }
    if (!/^#[0-9A-Fa-f]{6}$/.test(color)) {
      alert('Cor deve ser um código hexadecimal válido (#RRGGBB)');
      return;
    }
    onSave({ name: name.trim(), color });
  };

  const handleInputFocus = (inputId: string) => {
    setFocusedInput(inputId);
  };

  const handleInputBlur = () => {
    setFocusedInput(null);
  };

  const styles = {
    label: {
      fontFamily: "'Poppins', sans-serif",
      fontSize: '12px',
      fontWeight: 500,
      color: '#2D3748',
      marginBottom: '4px',
      display: 'block',
    } as React.CSSProperties,
    input: {
      fontFamily: "'Poppins', sans-serif",
      fontSize: '12px',
      color: '#2D3748',
      width: '100%',
      minHeight: 40,
      padding: '10px 14px',
      borderRadius: 10,
      border: '1px solid #E2E8F0',
      backgroundColor: '#FFFFFF',
      outline: 'none',
      transition: 'border-color 0.2s, box-shadow 0.2s',
    } as React.CSSProperties,
    inputFocus: {
      borderColor: '#06141B',
      boxShadow: '0 0 0 3px rgba(6, 20, 27, 0.12)',
    } as React.CSSProperties,
    inputError: {
      borderColor: '#EF4444',
    } as React.CSSProperties,
    error: {
      fontFamily: "'Poppins', sans-serif",
      fontSize: '11px',
      color: '#EF4444',
      marginTop: '4px',
    } as React.CSSProperties,
  };

  const modalContent = (
    <div 
      className="fixed inset-0 flex items-center justify-center z-[9999] p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tag-modal-title"
    >
      <div 
        className="bg-white dark:bg-gray-800 max-w-md w-full flex flex-col max-h-[90vh] overflow-hidden"
        style={{
          fontFamily: "'Poppins', sans-serif",
          borderRadius: 15,
          boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header – padrão da plataforma */}
        <div
          className="flex items-center justify-between gap-4 flex-shrink-0 p-5 pb-4 dark:border-gray-700"
          style={{ borderBottom: '1px solid rgba(6, 20, 27, 0.08)' }}
        >
          <h3 
            id="tag-modal-title"
            className="dark:text-gray-100"
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: 18,
              fontWeight: 600,
              color: '#06141B',
            }}
          >
            {tag ? 'Editar Tag' : 'Nova Tag'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-[10px] transition-opacity hover:opacity-80"
            style={{ backgroundColor: '#06141B', color: '#F5F6F5' }}
            aria-label="Fechar modal"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-y-auto p-5">
          <div className="space-y-5">
            <div>
              <label 
                htmlFor="name" 
                className="dark:text-gray-300"
                style={styles.label}
              >
                Nome <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onFocus={() => handleInputFocus('name')}
                onBlur={handleInputBlur}
                className="dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 dark:placeholder-gray-400"
                style={{
                  ...styles.input,
                  ...(focusedInput === 'name' ? styles.inputFocus : {}),
                }}
                required
                minLength={2}
                maxLength={40}
                placeholder="Ex: Ação de Cobrança"
              />
            </div>
            <div>
              <label className="dark:text-gray-300" style={styles.label}>
                Cor <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: 11, color: '#6B7280', marginBottom: 8 }}>
                Sugestões
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                {TAG_COLOR_PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className="w-8 h-8 rounded-[10px] flex-shrink-0 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#06141B]"
                    style={{
                      backgroundColor: c,
                      border: color.toLowerCase() === c.toLowerCase() ? '2px solid #06141B' : '2px solid #E2E8F0',
                      boxShadow: color.toLowerCase() === c.toLowerCase() ? '0 0 0 1px #06141B' : undefined,
                    }}
                    title={c}
                  />
                ))}
              </div>
              <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: 11, color: '#6B7280', marginBottom: 6 }}>
                Personalizado
              </p>
              <div className="flex items-center gap-3">
                <input
                  ref={colorInputRef}
                  type="color"
                  id="tag-color-picker"
                  value={/^#[0-9A-Fa-f]{6}$/.test(color) ? color : '#3B82F6'}
                  onChange={(e) => setColor(e.target.value)}
                  className="sr-only"
                  aria-hidden
                />
                <button
                  type="button"
                  onClick={() => colorInputRef.current?.click()}
                  className="w-10 h-10 rounded-[10px] flex-shrink-0 border border-[#E2E8F0] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#06141B] focus:ring-offset-2"
                  style={{ backgroundColor: /^#[0-9A-Fa-f]{6}$/.test(color) ? color : '#3B82F6' }}
                  title="Abrir seletor de cor"
                />
                <input
                  type="text"
                  value={color}
                  onChange={(e) => setColor(normalizedHex(e.target.value) || e.target.value)}
                  onFocus={() => handleInputFocus('color')}
                  onBlur={handleInputBlur}
                  className="flex-1 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 dark:placeholder-gray-400"
                  style={{
                    ...styles.input,
                    ...(focusedInput === 'color' ? styles.inputFocus : {}),
                  }}
                  placeholder="#3B82F6"
                  pattern="^#[0-9A-Fa-f]{6}$"
                />
              </div>
              <p className="mt-1 dark:text-gray-400" style={{ fontFamily: "'Poppins', sans-serif", fontSize: 11, color: '#6B7280' }}>
                Formato: #RRGGBB
              </p>
            </div>
          </div>

          {/* Footer – botões no padrão da plataforma */}
          <div className="flex gap-3 pt-5 border-t border-gray-100 dark:border-gray-700 flex-shrink-0">
            <button
              type="submit"
              disabled={isSaving}
              className="flex-1 px-5 py-3 rounded-[10px] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: 12,
                fontWeight: 600,
                color: '#FFFFFF',
                backgroundColor: '#06141B',
                border: 'none',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 8px rgba(6, 20, 27, 0.2)',
              }}
              onMouseEnter={(e) => {
                if (!isSaving) {
                  e.currentTarget.style.backgroundColor = '#0A1F29';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(6, 20, 27, 0.25)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#06141B';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(6, 20, 27, 0.2)';
              }}
            >
              {isSaving ? 'Salvando...' : 'Salvar'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-5 py-3 rounded-[10px] transition-colors"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: 12,
                fontWeight: 600,
                color: '#2D3748',
                backgroundColor: '#FFFFFF',
                border: '1px solid #E2E8F0',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F9FAFB';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#FFFFFF';
              }}
              disabled={isSaving}
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

